import React, { useState } from 'react';

export default function AdminBestPracticesSection({ appId }) {
  const [expandedSections, setExpandedSections] = useState({
    versionChecker: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span>✨</span>
          <span>App Best Practices</span>
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Recommended patterns and components for app development
        </p>
      </div>

      <div className="space-y-4">
        {/* VersionChecker Configuration */}
        <div className="border border-gray-200 rounded-lg">
          <button
            onClick={() => toggleSection('versionChecker')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
          >
            <h4 className="text-sm font-semibold text-gray-900">VersionChecker - Auto Update Detection</h4>
            <span className="text-gray-500 text-sm">
              {expandedSections.versionChecker ? '▼' : '▶'}
            </span>
          </button>
          {expandedSections.versionChecker && (
            <div className="p-4 pt-4 border-t border-gray-200 bg-gray-50">
              <div className="space-y-4 text-sm text-gray-700">
                <div>
                  <p className="text-xs text-gray-600 mb-3">
                    Detects when a new app version is deployed by comparing script file hashes and prompts users to reload.
                  </p>
                  
                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">How It Works</h5>
                    <ol className="list-decimal list-inside space-y-2 text-xs text-gray-600">
                      <li>Captures script URLs from the current page</li>
                      <li>Periodically fetches the root HTML (no-cache) to get latest script URLs</li>
                      <li>Compares them — if different, shows a reload prompt</li>
                    </ol>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">Code Implementation</h5>
                    <pre className="text-xs text-gray-800 font-mono bg-gray-50 px-3 py-3 rounded border border-gray-300 overflow-x-auto whitespace-pre-wrap">
{`import React, { useEffect, useState } from 'react';
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button"; // or your button component

export default function VersionChecker() {
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    // Extract script sources from DOM
    const getScriptSources = (doc) => {
      return Array.from(doc.getElementsByTagName('script'))
        .map(s => s.src)
        .filter(src => src && src.includes('/assets/') && !src.includes('chrome-extension'));
    };

    const initialScripts = JSON.stringify(getScriptSources(document));

    const checkForUpdates = async () => {
      try {
        // Fetch latest HTML with no cache
        const response = await fetch('/', { 
          cache: 'no-store',
          headers: { 'Pragma': 'no-cache' }
        });
        
        if (!response.ok) return;
        
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const latestScripts = JSON.stringify(getScriptSources(doc));

        // Compare scripts - if different, update available
        if (initialScripts !== latestScripts && latestScripts !== '[]') {
          setHasUpdate(true);
        }
      } catch (error) {
        console.error('Failed to check for updates:', error);
      }
    };

    // Check every 5 minutes + on window focus
    const intervalId = setInterval(checkForUpdates, 5 * 60 * 1000);
    window.addEventListener('focus', checkForUpdates);
    const initialTimeout = setTimeout(checkForUpdates, 10000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(initialTimeout);
      window.removeEventListener('focus', checkForUpdates);
    };
  }, []);

  useEffect(() => {
    if (hasUpdate) {
      toast.custom((t) => (
        <div className="bg-slate-900 text-white p-4 rounded-lg shadow-xl border border-slate-700 flex items-center justify-between gap-4 min-w-[320px]">
          <div className="flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-blue-400" />
            <div>
              <p className="font-semibold text-sm">Update Available</p>
              <p className="text-xs text-slate-400">A new version is ready.</p>
            </div>
          </div>
          <Button 
            size="sm" 
            onClick={() => window.location.reload()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Reload Now
          </Button>
        </div>
      ), { 
        duration: Infinity, 
        id: 'app-update-toast',
        position: 'bottom-right'
      });
    }
  }, [hasUpdate]);

  return null;
}`}
                    </pre>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">Usage</h5>
                    <p className="text-xs text-gray-600 mb-2">Add to your root layout or App component:</p>
                    <pre className="text-xs text-gray-800 font-mono bg-gray-50 px-3 py-3 rounded border border-gray-300 overflow-x-auto whitespace-pre-wrap">
{`import VersionChecker from "@/components/common/VersionChecker";
import { Toaster } from 'sonner';

function App() {
  return (
    <>
      {/* Your app content */}
      <VersionChecker />
      <Toaster />
    </>
  );
}`}
                    </pre>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">Dependencies</h5>
                    <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                      <li><code className="bg-gray-200 px-1 rounded">sonner</code> (or any toast library)</li>
                      <li>Your button component (or native button)</li>
                      <li>Icon library (optional)</li>
                    </ul>
                  </div>

                  <div className="mb-4">
                    <h5 className="font-semibold text-gray-900 mb-2">Customization</h5>
                    <ul className="text-xs text-gray-600 list-disc list-inside space-y-1">
                      <li>Change <code className="bg-gray-200 px-1 rounded">/assets/</code> filter (line 18) to match your build output path</li>
                      <li>Adjust polling interval: <code className="bg-gray-200 px-1 rounded">5 * 60 * 1000</code> = 5 minutes</li>
                      <li>Modify toast UI to match your design system</li>
                    </ul>
                    <p className="text-xs text-gray-500 mt-2 italic">
                      Works with any bundler (Vite, Webpack, etc.) that generates hashed filenames.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
